/**
 * SecureEmbed Connections Network Graph
 *
 * Cytoscape.js force-directed graph component for visualising cross-domain
 * entity relationships. Nodes represent records from different datasources,
 * edges represent semantic similarity or cross-domain correlations.
 *
 * Node styling by datasource (colour from DATASOURCE_COLORS):
 *   AEMO Energy:         hexagon          #F59E0B
 *   AEMO DUID:           roundrectangle   #EAB308
 *   AEMO FCAS:           roundrectangle   #CA8A04
 *   BOM Weather:         ellipse          #3B82F6
 *   AER Regulatory:      diamond          #10B981
 *   Regulatory Docs:     diamond          #059669
 *   Scholarly Research:  star             #8B5CF6
 *   Partner Discovery:   pentagon         #EC4899
 *   Quantum Ecosystem:   pentagon         #6366F1
 *
 * Edge styling:
 *   similarity:          dashed purple
 *   correlation:         solid amber
 *   reference:           dotted teal
 *   cross-domain:        solid blue directed
 *
 * Interactions: click select, hover highlight neighbourhood,
 *               scroll zoom, drag pan, fullscreen
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
// @ts-ignore — no @types/react-cytoscapejs package available
import CytoscapeComponent from 'react-cytoscapejs';
import type cytoscape from 'cytoscape';
import {
  ZoomIn, ZoomOut, Maximize2, Minimize2, X, Eye, EyeOff, Info,
  Clock, Database, Sparkles, ExternalLink, Search, Loader2,
} from 'lucide-react';
import { DATASOURCE_COLORS } from '../constants/colors';
import DataSourceBadge from './DataSourceBadge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type AnalysisNodeType =
  | 'aemo' | 'aemo-duid' | 'aemo-fcas' | 'bom' | 'aer'
  | 'regulatory' | 'scholarly' | 'partner'
  | 'insight' | 'anomaly';

export type AnalysisEdgeType =
  | 'similarity' | 'correlation' | 'reference' | 'cross-domain';

export interface AnalysisGraphNode {
  id: string;
  label: string;
  type: AnalysisNodeType;
  score?: number;
  content?: string;
  timestamp?: string;
  region?: string;
  crossSourceCount?: number;
}

export interface AnalysisGraphEdge {
  id?: string;
  source: string;
  target: string;
  type: AnalysisEdgeType;
  weight?: number;
  label?: string;
}

export type LayoutType = 'cose' | 'circle' | 'grid' | 'breadthfirst';

export interface ConnectionsGraphProps {
  nodes: AnalysisGraphNode[];
  edges: AnalysisGraphEdge[];
  onNodeClick?: (node: AnalysisGraphNode) => void;
  onOpenSource?: (node: AnalysisGraphNode) => void;
  onFindRelated?: (node: AnalysisGraphNode) => void;
  onExpandNode?: (nodeId: string, nodeType: string) => void;
  layout?: LayoutType;
  selectedNodeId?: string;
  expandedNodeIds?: Set<string>;
  className?: string;
  findingRelated?: boolean;
  expanding?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_VISIBLE_NODES = 500;

const NODE_TYPE_CONFIG: Record<string, { label: string; shape: string; color: string; size: number }> = {
  aemo:               { label: 'AEMO Energy',        shape: 'hexagon',        color: DATASOURCE_COLORS.aemo?.hex        || '#F59E0B', size: 55 },
  'aemo-duid':        { label: 'AEMO DUID',          shape: 'roundrectangle', color: DATASOURCE_COLORS['aemo-duid']?.hex || '#EAB308', size: 40 },
  'aemo-fcas':        { label: 'AEMO FCAS',          shape: 'roundrectangle', color: DATASOURCE_COLORS['aemo-fcas']?.hex || '#CA8A04', size: 40 },
  bom:                { label: 'BOM Weather',        shape: 'ellipse',        color: DATASOURCE_COLORS.bom?.hex         || '#3B82F6', size: 50 },
  aer:                { label: 'AER Regulatory',     shape: 'diamond',        color: DATASOURCE_COLORS.aer?.hex         || '#10B981', size: 45 },
  regulatory:         { label: 'Regulatory Docs',    shape: 'diamond',        color: '#059669', size: 40 },
  scholarly:          { label: 'Scholarly Research',  shape: 'star',           color: '#8B5CF6', size: 50 },
  partner:            { label: 'Partner & Quantum',   shape: 'pentagon',       color: '#EC4899', size: 45 },
  insight:            { label: 'Generated Insight',   shape: 'star',           color: '#7C3AED', size: 35 },
  anomaly:            { label: 'Anomaly Detection',   shape: 'triangle',       color: '#EF4444', size: 40 },
};

// ---------------------------------------------------------------------------
// Detail Panel
// ---------------------------------------------------------------------------
const DetailPanel: React.FC<{
  node: AnalysisGraphNode | null;
  connectionCount: number;
  onClose: () => void;
  onOpenSource?: (node: AnalysisGraphNode) => void;
  onFindRelated?: (node: AnalysisGraphNode) => void;
  findingRelated?: boolean;
}> = ({ node, connectionCount, onClose, onOpenSource, onFindRelated, findingRelated }) => {
  if (!node) return null;
  const config = NODE_TYPE_CONFIG[node.type] || NODE_TYPE_CONFIG.aemo;

  return (
    <div className="absolute top-0 right-0 w-80 h-full bg-white border-l border-gray-200 shadow-lg z-20 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Node Details</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-4 h-4 rounded-sm shrink-0 mt-0.5" style={{ backgroundColor: config.color }} />
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wide">{config.label}</span>
            <h4 className="text-sm font-semibold text-gray-900 mt-0.5">{node.label}</h4>
          </div>
        </div>

        {node.content && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Content</p>
            <p className="text-sm text-gray-500 leading-relaxed line-clamp-6">{node.content}</p>
          </div>
        )}

        {node.score != null && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Relevance Score</p>
            <span className="text-sm font-semibold text-gray-700">{(node.score * 100).toFixed(0)}%</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <DataSourceBadge source={node.type} size="sm" />
        </div>

        {node.timestamp && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" /> {new Date(node.timestamp).toLocaleDateString()}
          </div>
        )}

        {node.region && (
          <div className="text-xs text-gray-500">Region: {node.region}</div>
        )}

        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Database className="w-3 h-3" /> {connectionCount} connection{connectionCount !== 1 ? 's' : ''}
        </div>

        {node.crossSourceCount != null && node.crossSourceCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-purple-600">
            <Sparkles className="w-3 h-3" /> {node.crossSourceCount} cross-source link{node.crossSourceCount !== 1 ? 's' : ''}
          </div>
        )}

        {/* Action buttons */}
        <div className="pt-2 border-t border-gray-100 space-y-2">
          {onOpenSource && (
            <button
              onClick={() => onOpenSource(node)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Source Record
            </button>
          )}
          {onFindRelated && (
            <button
              onClick={() => onFindRelated(node)}
              disabled={findingRelated}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {findingRelated ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Search className="w-3.5 h-3.5" />
              )}
              {findingRelated ? 'Finding...' : 'Find Related Records'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cytoscape stylesheet
// ---------------------------------------------------------------------------
const buildStylesheet = (): any[] => {
  const sheets: any[] = [
    // Base node
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'font-size': '9px',
        'text-wrap': 'wrap',
        'text-max-width': '100px',
        color: '#374151',
        'text-margin-y': 6,
        'background-opacity': 0.85,
        'border-width': 1.5,
      },
    },
    // Base edge
    {
      selector: 'edge',
      style: {
        width: 1.5,
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
        'arrow-scale': 0.7,
        opacity: 0.6,
      },
    },
    // Edge types
    {
      selector: 'edge[type="similarity"]',
      style: { 'line-color': '#7C3AED', 'line-style': 'dashed', width: 2 },
    },
    {
      selector: 'edge[type="correlation"]',
      style: { 'line-color': '#F59E0B', 'line-style': 'solid', width: 2 },
    },
    {
      selector: 'edge[type="reference"]',
      style: { 'line-color': '#14B8A6', 'line-style': 'dotted', width: 1.5 },
    },
    {
      selector: 'edge[type="cross-domain"]',
      style: {
        'line-color': '#3B82F6',
        'target-arrow-color': '#3B82F6',
        'target-arrow-shape': 'triangle',
        'line-style': 'solid',
        width: 2,
      },
    },
    // Selected
    {
      selector: 'node:selected',
      style: {
        'border-width': 4,
        'border-color': '#2B064F',
        'overlay-opacity': 0.15,
        'overlay-color': '#2B064F',
      },
    },
    // Hover highlight
    { selector: 'node.highlighted', style: { 'border-width': 3, 'border-color': '#2B064F' } },
    { selector: 'edge.highlighted', style: { opacity: 1, width: 3 } },
    { selector: 'node.faded', style: { opacity: 0.12 } },
    { selector: 'edge.faded', style: { opacity: 0.06 } },
    { selector: 'node:active', style: { 'overlay-color': '#2B064F', 'overlay-opacity': 0.2 } },
    // Expanded nodes get a dashed border to show they've been explored
    { selector: 'node[?expanded]', style: { 'border-width': 2.5, 'border-style': 'dashed' as any, 'border-color': '#94A3B8' } },
  ];

  // Per-type node styles
  for (const [type, config] of Object.entries(NODE_TYPE_CONFIG)) {
    sheets.push({
      selector: `node[type="${type}"]`,
      style: {
        shape: config.shape as any,
        width: config.size,
        height: config.size,
        'background-color': config.color,
        'border-color': config.color,
      },
    });
  }

  return sheets;
};

const CYTOSCAPE_STYLESHEET = buildStylesheet();

// ---------------------------------------------------------------------------
// Layout configs
// ---------------------------------------------------------------------------
const LAYOUT_CONFIGS: Record<string, cytoscape.LayoutOptions> = {
  cose: {
    name: 'cose',
    animate: true,
    animationDuration: 500,
    nodeRepulsion: () => 10000,
    idealEdgeLength: () => 120,
    nodeOverlap: 20,
    gravity: 0.2,
    padding: 50,
  } as cytoscape.LayoutOptions,
  breadthfirst: {
    name: 'breadthfirst',
    directed: false,
    spacingFactor: 1.5,
    padding: 50,
    animate: true,
    animationDuration: 500,
  } as cytoscape.LayoutOptions,
  circle: {
    name: 'circle',
    padding: 50,
    animate: true,
    animationDuration: 500,
  } as cytoscape.LayoutOptions,
  grid: {
    name: 'grid',
    padding: 50,
    animate: true,
    animationDuration: 500,
    rows: 4,
  } as cytoscape.LayoutOptions,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ConnectionsGraph: React.FC<ConnectionsGraphProps> = ({
  nodes,
  edges,
  onNodeClick,
  onOpenSource,
  onFindRelated,
  onExpandNode,
  layout = 'cose',
  selectedNodeId,
  expandedNodeIds,
  className = '',
  findingRelated,
  expanding,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<{ node: AnalysisGraphNode; count: number } | null>(null);
  const [visibleTypes, setVisibleTypes] = useState<Set<string>>(() => new Set(Object.keys(NODE_TYPE_CONFIG)));
  const cyRef = useRef<cytoscape.Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter by visible types
  const filteredNodes = useMemo(() => nodes.filter(n => visibleTypes.has(n.type)).slice(0, MAX_VISIBLE_NODES), [nodes, visibleTypes]);
  const nodeIdSet = useMemo(() => new Set(filteredNodes.map(n => n.id)), [filteredNodes]);
  const filteredEdges = useMemo(() => edges.filter(e => nodeIdSet.has(e.source) && nodeIdSet.has(e.target)), [edges, nodeIdSet]);

  // Active types (only those that exist in data)
  const activeTypes = useMemo(() => {
    const types = new Set<string>();
    for (const n of nodes) types.add(n.type);
    return types;
  }, [nodes]);

  // Cytoscape elements
  const elements = useMemo(() => [
    ...filteredNodes.map(node => ({
      data: {
        id: node.id,
        label: node.label.length > 40 ? node.label.slice(0, 37) + '...' : node.label,
        fullLabel: node.label,
        type: node.type,
        score: node.score || 0,
        content: node.content || '',
        timestamp: node.timestamp || '',
        region: node.region || '',
        crossSourceCount: node.crossSourceCount || 0,
        expanded: expandedNodeIds?.has(node.id) ? true : undefined,
      },
    })),
    ...filteredEdges.map((edge, i) => ({
      data: {
        id: edge.id || `edge-${i}`,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        weight: edge.weight || 0.5,
        label: edge.label || '',
      },
    })),
  ], [filteredNodes, filteredEdges, expandedNodeIds]);

  const handleCyReady = useCallback((cy: cytoscape.Core) => {
    cyRef.current = cy;

    cy.on('tap', 'node', (evt) => {
      const d = evt.target.data();
      const nodeObj: AnalysisGraphNode = {
        id: d.id,
        label: d.fullLabel || d.label,
        type: d.type,
        score: d.score,
        content: d.content,
        timestamp: d.timestamp,
        region: d.region,
        crossSourceCount: d.crossSourceCount,
      };
      const connCount = cy.getElementById(d.id).connectedEdges().length;
      setSelectedDetail({ node: nodeObj, count: connCount });
      if (onNodeClick) onNodeClick(nodeObj);
      if (onExpandNode) onExpandNode(d.id, d.type);
    });

    // Hover neighbourhood highlight
    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target;
      const neighbourhood = node.closedNeighborhood();
      cy.elements().addClass('faded');
      neighbourhood.removeClass('faded').addClass('highlighted');
    });

    cy.on('mouseout', 'node', () => {
      cy.elements().removeClass('faded').removeClass('highlighted');
    });

    if (selectedNodeId) {
      const selected = cy.getElementById(selectedNodeId);
      if (selected.length) {
        selected.select();
        cy.animate({ center: { eles: selected }, zoom: 1.5, duration: 300 });
      }
    }
  }, [onNodeClick, onExpandNode, selectedNodeId]);

  const handleZoomIn = () => { if (cyRef.current) cyRef.current.zoom(cyRef.current.zoom() * 1.3); };
  const handleZoomOut = () => { if (cyRef.current) cyRef.current.zoom(cyRef.current.zoom() / 1.3); };
  const handleFit = () => { if (cyRef.current) cyRef.current.fit(undefined, 30); };
  const handleFullscreen = () => {
    if (!containerRef.current) return;
    if (!isFullscreen) containerRef.current.requestFullscreen?.();
    else document.exitFullscreen?.();
    setIsFullscreen(!isFullscreen);
  };

  const toggleType = (type: string) => {
    setVisibleTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const layoutConfig = LAYOUT_CONFIGS[layout] || LAYOUT_CONFIGS.cose;

  return (
    <div
      ref={containerRef}
      className={`relative border border-gray-200 rounded-xl bg-white overflow-hidden ${className}`}
      style={{ minHeight: '550px' }}
    >
      {/* Performance warning */}
      {nodes.length > MAX_VISIBLE_NODES && (
        <div className="absolute top-2 left-2 z-20 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs text-amber-700">
          Showing {MAX_VISIBLE_NODES} of {nodes.length} nodes
        </div>
      )}

      {/* Node type legend */}
      <div className="absolute top-3 left-3 z-20 bg-white/95 backdrop-blur rounded-xl border border-gray-200 shadow-sm p-3 max-w-[200px]">
        <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Entity Types</h4>
        <div className="space-y-1">
          {Object.entries(NODE_TYPE_CONFIG)
            .filter(([type]) => activeTypes.has(type))
            .map(([type, config]) => {
              const visible = visibleTypes.has(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`flex items-center gap-2 w-full px-1.5 py-1 rounded text-left transition-opacity ${visible ? '' : 'opacity-30'}`}
                >
                  <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: config.color }} />
                  <span className="text-[10px] text-gray-700 flex-1 truncate">{config.label}</span>
                  {visible ? <Eye className="w-2.5 h-2.5 text-gray-400" /> : <EyeOff className="w-2.5 h-2.5 text-gray-300" />}
                </button>
              );
            })}
        </div>
      </div>

      {/* Edge type legend */}
      <div className="absolute bottom-3 left-3 z-20 bg-white/90 backdrop-blur rounded-lg px-3 py-2 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-4 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-4 h-0 border-t-2 border-dashed border-purple-500 inline-block" />Similarity</span>
          <span className="flex items-center gap-1"><span className="w-4 h-0 border-t-2 border-solid border-amber-500 inline-block" />Correlation</span>
          <span className="flex items-center gap-1"><span className="w-4 h-0 border-t-2 border-dotted border-teal-500 inline-block" />Reference</span>
          <span className="flex items-center gap-1"><span className="w-4 h-0 border-t-2 border-solid border-blue-500 inline-block" />Cross-domain</span>
          <span className="text-gray-400">|</span>
          <span>{filteredNodes.length} nodes, {filteredEdges.length} edges</span>
        </div>
      </div>

      {/* Graph canvas */}
      {elements.length > 0 ? (
        <CytoscapeComponent
          elements={elements}
          stylesheet={CYTOSCAPE_STYLESHEET}
          layout={layoutConfig}
          cy={(cy: cytoscape.Core) => handleCyReady(cy)}
          style={{ width: '100%', height: '100%', minHeight: '550px' }}
          className="w-full h-full"
          minZoom={0.15}
          maxZoom={3}
          wheelSensitivity={0.3}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Info className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No connections to display</p>
            <p className="text-xs text-gray-400 mt-1">Run a search to discover cross-domain relationships</p>
          </div>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-1.5 z-20">
        {[
          { icon: ZoomIn, action: handleZoomIn, title: 'Zoom in' },
          { icon: ZoomOut, action: handleZoomOut, title: 'Zoom out' },
          { icon: Maximize2, action: handleFit, title: 'Fit to screen' },
          { icon: isFullscreen ? Minimize2 : Maximize2, action: handleFullscreen, title: isFullscreen ? 'Exit fullscreen' : 'Fullscreen' },
        ].map(({ icon: Icon, action, title }, i) => (
          <button
            key={i}
            onClick={action}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-purple-50 hover:border-purple-300 transition-colors"
            title={title}
          >
            <Icon className="w-4 h-4 text-gray-600" />
          </button>
        ))}
      </div>

      {/* Expanding indicator */}
      {expanding && (
        <div className="absolute bottom-12 right-4 z-20 flex items-center gap-2 bg-white/95 border border-purple-200 rounded-lg px-3 py-1.5 shadow-sm">
          <Loader2 className="w-3.5 h-3.5 text-purple-600 animate-spin" />
          <span className="text-xs text-purple-700 font-medium">Expanding...</span>
        </div>
      )}

      {/* Detail Side Panel */}
      <DetailPanel
        node={selectedDetail?.node || null}
        connectionCount={selectedDetail?.count || 0}
        onClose={() => setSelectedDetail(null)}
        onOpenSource={onOpenSource}
        onFindRelated={onFindRelated}
        findingRelated={findingRelated}
      />
    </div>
  );
};

export default ConnectionsGraph;
